import lodash from 'lodash';

import {
    daysSinceNowUTC,
    daysBetweenDatesUTC
} from 'common/util/dateTime';

import {
    EVENT_TYPE_TASK,
    DOA_TYPE_TASK,
    SDFA_TYPE_TASK
} from 'common/util/taskHelper';

import { getDateInInstitutionTZ } from 'common/util/timezoneFormat';

export const getAllPendingStudyTasks = (state) => {
    const pendingEvents = lodash.get(state, 'study.tasks.pending.events', []);
    const pendingDOA = lodash.get(state, 'study.tasks.pending.delegations', []);
    const pendingSFDA = lodash.get(state, 'study.tasks.pending.documents', []);
    const institutionTimezone = lodash.get(state, 'me.institutionTimezone');

    const formattedPendingEvents = pendingEvents.map(pendingEvent => formatPendingSignatures(
        pendingEvent.id,
        lodash.get(pendingEvent, 'event.eventType', undefined),
        'Event',
        lodash.get(pendingEvent, 'createdBy.displayName', undefined),
        getDateInInstitutionTZ(institutionTimezone, lodash.get(pendingEvent, 'sentDate')),
        EVENT_TYPE_TASK,
        pendingEvent.activeInstances = 1,
        pendingEvent.completedInstances = 0,
        pendingEvent.totalInstances = 1
    ));

    const formattedPendingDOA = pendingDOA.map(pendingDOA => formatPendingSignatures(
        lodash.get(pendingDOA, 'id'),
        lodash.get(pendingDOA, 'delegation.user.displayName') +
        ' as ' +
        lodash.get(pendingDOA, 'delegation.rolesManagement.name'),
        'Delegation',
        lodash.get(pendingDOA, 'createdBy.displayName'),
        getDateInInstitutionTZ(institutionTimezone, lodash.get(pendingDOA, 'sentDate')),
        DOA_TYPE_TASK,
    ));

    const formattedPendingSFDA = pendingSFDA.map(task => formatPendingSignatures(
        task.id,
        task.title,
        task.document ? task.document.documentType.displayName : '',
        task.authorDisplayName,
        getDateInInstitutionTZ(institutionTimezone, task.sentDate),
        SDFA_TYPE_TASK,
        task.activeInstances,
        task.completedInstances,
        task.totalInstances,
    ));

    const pendingSortedBySentDate = [
        ...formattedPendingEvents,
        ...formattedPendingDOA,
        ...formattedPendingSFDA,
    ].sort((a, b) => {
        return new Date(a.sentDate) - new Date(b.sentDate);
    });

    return pendingSortedBySentDate;
};

export const getAllCompletedStudyTasks = (state) => {
    const completedEvents = lodash.get(state, 'study.tasks.completed.events', []);
    const completedDOA = lodash.get(state, 'study.tasks.completed.delegations', []);
    const completedSDFA = lodash.get(state, 'study.tasks.completed.documents', []);
    const institutionTimezone = lodash.get(state, 'me.institutionTimezone');

    const formattedCompletedEvents = completedEvents.map(completedEvent => formatCompletedSignatures(
        completedEvent.id,
        lodash.get(completedEvent, 'event.eventType', undefined),
        'Event',
        lodash.get(completedEvent, 'createdBy.displayName', undefined),
        getDateInInstitutionTZ(institutionTimezone, lodash.get(completedEvent, 'sentDate')),
        getDateInInstitutionTZ(institutionTimezone, lodash.get(completedEvent, 'signedDate')),
        EVENT_TYPE_TASK,
        completedEvent.completedInstances = 1
    ));

    const formattedCompletedDOA = completedDOA.map(completedDOA => formatCompletedSignatures(
        lodash.get(completedDOA, 'id'),
        lodash.get(completedDOA, 'delegation.user.displayName') +
        ' as ' +
        lodash.get(completedDOA, 'delegation.rolesManagement.name'),
        'Delegation',
        lodash.get(completedDOA, 'createdBy.displayName'),
        getDateInInstitutionTZ(institutionTimezone, lodash.get(completedDOA, 'sentDate')),
        getDateInInstitutionTZ(institutionTimezone, lodash.get(completedDOA, 'signedDate')),
        DOA_TYPE_TASK,
    ));

    const formattedCompletedSDFA = completedSDFA.map(completedTask => formatCompletedSignatures(
        completedTask.id,
        completedTask.title,
        completedTask.document ? completedTask.document.documentType.displayName : '',
        completedTask.authorDisplayName,
        getDateInInstitutionTZ(institutionTimezone, completedTask.sentDate),
        getDateInInstitutionTZ(institutionTimezone, completedTask.endDate),
        SDFA_TYPE_TASK,
        completedTask.completedInstances,
    ));


    const completedSortedByCompletedDate = [
        ...formattedCompletedEvents,
        ...formattedCompletedDOA,
        ...formattedCompletedSDFA,
    ].sort((a, b) => {
        return new Date(b.completedDate) - new Date(a.completedDate);
    });

    return completedSortedByCompletedDate;
};

export const getAllTasksCount = (state) => {
    const pendingEventCount = lodash.get(state, 'study.tasks.pending.events.length', 0);
    const completedEventCount = lodash.get(state, 'study.tasks.completed.events.length', 0);
    const pendingDOACount = lodash.get(state, 'study.tasks.pending.delegations.length', 0);
    const completedDOACount = lodash.get(state, 'study.tasks.completed.delegations.length', 0);
    const pendingSDFACount = lodash.get(state, 'study.tasks.pending.documents.length', 0);
    const completedSDFACount = lodash.get(state, 'study.tasks.completed.documents.length', 0);

    return {
        pending: pendingEventCount + pendingDOACount + pendingSDFACount,
        completed: completedEventCount + completedDOACount + completedSDFACount,
    };
};

function formatPendingSignatures(
    id,
    title,
    documentType,
    authorDisplayName,
    sentDate,
    taskType,
    activeInstances = 1,
    completedInstances = 0,
    totalInstances = 1
) {
    const daysSinceNow = daysSinceNowUTC(sentDate);

    return {
        id,
        title,
        documentType,
        authorDisplayName,
        sentDate: sentDate,
        daysSinceAssigned: daysSinceNow,
        taskType,
        activeInstances,
        completedInstances,
        totalInstances,
    };
}

function formatCompletedSignatures(
    id,
    title,
    documentType,
    authorDisplayName,
    sentDate,
    endDate,
    taskType,
    completedInstances = 1
) {
    const daysToComplete = Math.abs(daysBetweenDatesUTC(sentDate, endDate));

    return {
        id,
        title,
        documentType,
        authorDisplayName,
        sentDate,
        daysSinceAssigned: daysToComplete,
        endDate,
        taskType,
        completedInstances,
    };
}
